import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  catchError,
  distinctUntilChanged,
  of,
  shareReplay,
  switchMap,
  tap,
  filter,
} from 'rxjs';
import { environment } from 'environments/environment';
import { Product, ProductsPayload, ProductsResponse } from '../Models/productsRequest.model';
import { SystemColor } from '../Models/systemColor.model';
import { ActivatedRoute, Router } from '@angular/router';
import { MenuItem, MenuItemType, ParamType } from '../Models/menuItem.model';

@Injectable({
  providedIn: 'root'
})
export class ProductsService {
  private URL = `${environment.apiUrl}Products/`;
  public productsPayloadSubject = new BehaviorSubject<ProductsPayload | null>(null);
  private activeTopMenuIdSubject = new BehaviorSubject<string | null>(null);
  public activeTopMenuId$ = this.activeTopMenuIdSubject.asObservable();
  public productsPayload$ = this.productsPayloadSubject.asObservable().pipe(distinctUntilChanged());

  public products$ = this.productsPayload$.pipe(
    filter((payload): payload is ProductsPayload => payload !== null),
    switchMap(payload => {
      const isBackendSorting = payload.sortBy === SortOptions.NewArrivals ||
        payload.sortBy === SortOptions.PriceLowToHigh ||
        payload.sortBy === SortOptions.PriceHighToLow;

      if (isBackendSorting) {
        return this.http.post<ProductsResponse>(
          `${this.URL}GetProductList`,
          payload
        ).pipe(
          tap(productsResponse => {
            if (productsResponse) {
              productsResponse.products = this.calculateDiscount(productsResponse.products);
            }
          }),
          catchError(error => {
            console.error(error);
            return of(null); // or handle error appropriately
          })
        );
      } else {
        return this.http.post<ProductsResponse>(
          `${this.URL}GetProductList`,
          payload
        ).pipe(
          tap(productsResponse => {
            if (productsResponse) {
              productsResponse.products = this.calculateDiscount(productsResponse.products);
              if (payload.sortBy) {
                productsResponse.products = this.sortProducts(productsResponse.products, payload.sortBy);
              }
            }
          }),
          catchError(error => {
            console.error(error);
            return of(null); // or handle error appropriately
          })
        );
      }
    }),

  );
  private menuItemsSubject = new BehaviorSubject<MenuItem[] | null>(null);
  menuItems$ = this.menuItemsSubject.asObservable();
  private _http: any = null;
  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this._http = http;
    this.subscribeToPayloadChanges();
  }
  public setActiveTopLevelMenu(activeCategoryId: string) {
    this.activeTopMenuIdSubject.next(activeCategoryId);
  }
  getActiveTopCategoryId() {
    return this.activeTopMenuIdSubject.getValue();
  }
  getMenuItems(): Observable<MenuItem[]> {
    return this.http.get<MenuItem[]>(`${this.URL}GetMenu`).pipe(
      tap(res => {
        this.menuItemsSubject.next(res);
      })
    );
  }

  getActiveTopMenuId(activeCategoryId: string) {
    const menuItems = this.menuItemsSubject.getValue();
    let topMenuId;

    if (menuItems) {
      for (const menuItem of menuItems) {
        topMenuId = this.findTopLevelCategoryId(menuItem, activeCategoryId);
        if (topMenuId) {
          break;
        }
      }
    }

    return topMenuId;
  }

  findTopLevelCategoryId(menuItem, targetValue) {
    if (menuItem.params && menuItem.params[0] && menuItem.params[0].value == targetValue) {
      return menuItem.params[0].value;
    }
    if (menuItem.subMenu?.length) {
      for (const subMenuItem of menuItem.subMenu) {
        const result = this.findTopLevelCategoryId(subMenuItem, targetValue);
        if (result !== null) {
          return result;
        }
      }
    }
    return null;
  }

  public initProductsPayload() {
    this.route.queryParams.subscribe(queryParams => {
      // Get current payload to preserve existing categories if no categories in query params
      const currentPayload = this.productsPayloadSubject.getValue();

      let categories: string[] = [];

      // First priority: categories from URL query params
      if (queryParams['categories']) {
        categories = queryParams['categories'].split(',');
      }
      // Second priority: categories from current payload (for navigation back from brands)
      else if (currentPayload?.categories && currentPayload.categories.length > 0) {
        categories = currentPayload.categories;
      }
      // Third priority: default categories
      else {
        categories = ['1', '67'];
      }

      const newConfig: ProductsPayload = {
        pageSize: queryParams['pageSize'] || 44,
        pageNumber: queryParams['pageNumber'] || 1,
        brands: queryParams['brands'] ? queryParams['brands'].split(',') : [],
        tags: queryParams['tags'] ? queryParams['tags'].split(',') : [],
        sortBy: queryParams['sortBy'] || SortOptions.NewArrivals,
        categories: categories,
      };

      this.productsPayloadSubject.next(newConfig);
    });
  }
  initTopCategoryId() {
    const activeCategories = this.route.snapshot.queryParams['categories'];
    this.setActiveTopLevelMenu(activeCategories?.split(',')[0]);
  }
  getSystemColor(): Observable<SystemColor[]> {
    return this.http.get<SystemColor[]>(`${this.URL}GetSystemColor/`);
  }
  handleMenuItem(menuItem: MenuItem) {
    if (menuItem.itemType === MenuItemType.Category) {
      menuItem.params?.forEach(param => {
        if (param.paramType === ParamType.Category) {
          this.updatePayloadCategoriesFromMenu(param.value.toString());
          this.setActiveTopLevelMenu(param.value.toString());
        } else if (param.paramType === ParamType.Tag) {
          this.updatePayloadByType(param.value.toString(), 'tags', null);
        } else if (param.paramType === ParamType.Brand) {
          this.updatePayloadByType(param.value.toString(), 'brands', null);
        }
      });
    } else {
      // For Link type menu items (like brands), preserve the full category hierarchy
      const currentPayload = this.productsPayloadSubject.getValue();
      const queryParams: any = {};

      if (currentPayload && currentPayload.categories && currentPayload.categories.length > 0) {
        // Preserve the full category hierarchy
        queryParams.categories = currentPayload.categories.join(',');
      } else {
        // Fallback to top-level category if no payload
        const topCategoryId = this.activeTopMenuIdSubject.getValue();
        if (topCategoryId) {
          queryParams.categories = topCategoryId;
        }
      }

      this.router.navigate(['/main/products' + menuItem?.url], {
        queryParams,
        queryParamsHandling: 'merge',
      });
    }
  }

  updatePayloadCategoriesFromMenu(categoryId: string) {
    let payload: ProductsPayload | null = this.productsPayloadSubject.getValue();

    if (!payload) {
      payload = {
        pageSize: 44,
        pageNumber: 1,
        brands: [],
        tags: [],
        sortBy: SortOptions.NewArrivals,
        categories: [],
      };
    }

    // Check if the category is already in the payload to prevent duplicates
    const categoryExists = payload.categories?.includes(categoryId);

    if (categoryExists) {
      return;
    }

    // Check if this is a top-level category
    const isTopLevel = this.isTopLevelCategory(categoryId);

    if (isTopLevel) {
      // For top-level categories, replace the entire categories array
      payload.categories = [categoryId];
      payload.brands = [];
      payload.tags = [];
      payload.sortBy = SortOptions.NewArrivals;
      payload.pageNumber = 1;
    } else {
      // For sub-categories, keep the first element and replace the rest
      if (payload.categories && payload.categories.length > 0) {
        payload.categories = [payload.categories[0], categoryId];
      } else {
        payload.categories = [categoryId];
      }
      // Reset to first page when category filter changes
      payload.pageNumber = 1;
    }

    this.productsPayloadSubject.next(payload);
  }

  // Add a method to handle category removal from sub-menu
  removeCategoryFromPayload(categoryId: string) {
    let payload: ProductsPayload | null = this.productsPayloadSubject.getValue();

    if (!payload || !payload.categories) return;

    payload.categories = payload.categories.filter(id => id !== categoryId);
    // Reset to first page when category filter is removed
    payload.pageNumber = 1;
    this.productsPayloadSubject.next(payload);
  }

  isTopLevelCategory(id: string) {
    const menuItems = this.menuItemsSubject.getValue();
    return menuItems?.some(menuItem =>
      menuItem.params.some(param =>
        param.paramType === ParamType.Category && param.value.toString() === id
      )
    ) || false;
  }
  updatePayloadByType(id: string | number, type: string, isChecked: boolean | null = true) {
    let payload: ProductsPayload | null = this.productsPayloadSubject.getValue();
    if (payload) {
      const isFilterType = ['brands', 'categories', 'tags', 'seasons'].includes(type);

      switch (type) {
        case 'pageNumber':
        case 'sortBy': {
          payload[type] = +id;
          break;
        }
        default: {
          if (isChecked === null) {
            payload[type] = [id];
          } else if (isChecked) {
            payload[type] = [...(payload[type] || []), id];
          } else {
            payload[type] = (payload[type] || []).filter(existingId => existingId !== id);
          }
          // Reset to first page when any filter is applied
          if (isFilterType) {
            payload.pageNumber = 1;
          }
          break;
        }
      }
      this.productsPayloadSubject.next(payload);
    }
  }

  private previousPayload: ProductsPayload | null = null;

  subscribeToPayloadChanges() {
    this.productsPayloadSubject.subscribe(payload => {
      if (payload) {
        const shouldScrollToTop = this.shouldScrollToTop(this.previousPayload, payload);
        this.updateRouterState(payload, shouldScrollToTop);
        this.previousPayload = { ...payload };
      }
    });
  };

  private shouldScrollToTop(previous: ProductsPayload | null, current: ProductsPayload): boolean {
    if (!previous) return false;

    // Scroll to top when categories or page change, but not when only brands/tags toggle
    // so users can select multiple brands without the page jumping
    const categoriesChanged =
      JSON.stringify(previous.categories || []) !== JSON.stringify(current.categories || []);
    const pageChanged = (previous.pageNumber ?? 1) !== (current.pageNumber ?? 1);
    return categoriesChanged || pageChanged;
  }

  updateRouterState(payload, shouldScrollToTop: boolean = false) {
    const queryParams: any = {};
    queryParams.pageNumber = payload.pageNumber;
    ['brands', 'tags', 'sortBy'].forEach(key => {
      if (Array.isArray(payload[key])) {
        queryParams[key] = payload[key].length ? payload[key].join(',') : null;
      } else {
        queryParams[key] = payload[key] || null;
      }
    });

    // Handle categories separately - only include if they exist
    if (Array.isArray(payload.categories) && payload.categories.length > 0) {
      queryParams.categories = payload.categories.join(',');
    }
    // Don't set categories to null if empty - preserve existing URL categories

    // Get the current URL
    const currentUrl = this.router.url;

    // Check if the current URL contains the target path ('products/list')
    if (currentUrl.includes('/products/list')) {
      // Only update router state if we're not in the middle of a navigation
      // This prevents circular updates that might clear categories
      const currentQueryParams = this.route.snapshot.queryParams;
      const hasCategoriesInUrl = currentQueryParams['categories'];
      const hasCategoriesInPayload = payload.categories && payload.categories.length > 0;

      // If we have categories in payload but not in URL, or if we're updating other params, proceed
      if (hasCategoriesInPayload || !hasCategoriesInUrl || Object.keys(queryParams).some(key => key !== 'categories')) {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParamsHandling: 'merge',
          queryParams,
        }).then(() => {
          // Scroll to top when filters are applied
          if (shouldScrollToTop && typeof window !== 'undefined') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        });
      }
    }
  }
  /** Default category chain (e.g. Women > Bags) used when resetting filters. */
  private static readonly DEFAULT_CATEGORIES = ['1', '67'];

  /**
   * Reset all filters to default (default category, no brands/tags/seasons).
   * Used by "Remove All" so the category section is never cleared.
   */
  resetFilters() {
    this.setActiveTopLevelMenu(ProductsService.DEFAULT_CATEGORIES[0]);
    const newPayload: ProductsPayload = {
      pageSize: 44,
      pageNumber: 1,
      brands: [],
      tags: [],
      sortBy: SortOptions.NewArrivals,
      categories: [...ProductsService.DEFAULT_CATEGORIES],
    };
    this.productsPayloadSubject.next(newPayload);
  }

  /**
   * Reset filters and sorting to the default Women > Bags category.
   * This is used for global navigation actions like clicking the logo.
   */
  resetToDefaultWomenBags() {
    this.setActiveTopLevelMenu(ProductsService.DEFAULT_CATEGORIES[0]);
    const newPayload: ProductsPayload = {
      pageSize: 44,
      pageNumber: 1,
      brands: [],
      tags: [],
      sortBy: SortOptions.NewArrivals,
      categories: [...ProductsService.DEFAULT_CATEGORIES],
    };
    this.productsPayloadSubject.next(newPayload);
  }

  /**
   * Completely reset all product service state including filters and active menu.
   * This should be called when user logs out to prevent state from persisting between users.
   */
  resetAllState() {
    // Reset active top menu
    this.activeTopMenuIdSubject.next(null);

    // Reset filters to empty/default state
    const resetPayload: ProductsPayload = {
      pageSize: 44,
      pageNumber: 1,
      brands: [],
      tags: [],
      sortBy: SortOptions.NewArrivals,
      categories: [],
    };
    this.productsPayloadSubject.next(resetPayload);

    // Clear previous payload tracking
    this.previousPayload = null;
  }

  getProductById(productId: number): Observable<any> {
    return this._http.get(`${this.URL}product/${productId}`).pipe(
      catchError(error => {
        console.error(error);
        return of(null);
      }),
      shareReplay(1)
    );
  }

  private calculateDiscount(products: Product[]) {
    return products.map((product: Product) => {
      if (product.prices[0]?.rrp) {
        const discount = 100 - ((product.prices[0]?.amount / product.prices[0]?.rrp) * 100);
        return { ...product, discount: discount || 0 };
      }
      return product;
    });
  }

  public sortProducts(products: Product[], val: SortOptions) {
    const sortValue = Number(val);
    switch (sortValue) {
      case SortOptions.HighestDiscount:
        return [...products].sort((a, b) => (b.discount || 0) - (a.discount || 0));
      case SortOptions.LowestDiscount:
        return [...products].sort((a, b) => (a.discount || 0) - (b.discount || 0));
      default:
        return products;
    }
  }
}

export type PayloadFilterType = 'pageNumber' | 'brands' | 'categories' | 'tags' | 'sortBy' | 'seasons';
export enum SortOptions {
  NewArrivals = 1,
  PriceHighToLow = 2,
  PriceLowToHigh = 3,
  HighestDiscount = 4,
  LowestDiscount = 5,
}
